import {TIME} from '@stubhub/general-utils';

/**
 * Map of delivery IDs to icon names.
 */
const DELIVERY_ID_ICON_MAP = {
  1: 'Download',
  2: 'Download',
  41: 'MobileTransfer',
  42: 'MobileTransfer',
  43: 'MobileTransfer',
  47: 'MobileTransfer',
  48: 'MobileTransfer',
  14: 'PaperTickets',
  22: 'PaperTickets',
  23: 'PaperTickets',
  24: 'PaperTickets',
  25: 'PaperTickets',
  27: 'PaperTickets',
  36: 'PaperTickets',
  37: 'PaperTickets',
  38: 'PaperTickets',
  39: 'PaperTickets',
  50: 'PaperTickets',
  default: 'GenericDisclosures',
};

// Delivery types
const DELIVERY_TYPE_IDS = {
  ELECTRONIC: 1,
  ELECTRONIC_INSTANT_DOWNLOAD: 2,
  FEDEX: 3,
  PICKUP: 4,
  UPS: 5,
  ROYAL_MAIL: 6,
  DEUTSCHE_POST: 7,
  MOBILEID: 8,
  MOBILE_TICKET: 9,
  MOBILE_TRANSFER: 10,
  COURIER: 11,
  MOBILE_TICKET_INSTANT: 12,
  MOBILEID_NON_INSTANT: 13,
  ELECTRONIC_AND_MOBILE_TICKET: 14,
  ELECTRONIC_AND_MOBILE_TICKET_INSTANT: 15,
  LOCAL_DELIVERY: 16,
};

/**
 * Map of delivery method IDs to their corresponding numerical identifiers.
 *
 * This object defines a set of delivery methods and their unique IDs
 * used in the system.
 *
 * For more details, refer to the documentation:
 * {@link https://wiki.stubcorp.cloud/pages/viewpage.action?pageId=44707642}
 *
 */
const DELIVERY_METHOD_IDS = {
  ELECTRONIC_DOWNLOAD: 1,
  ELECTRONIC_INSTANT_DOWNLOAD: 2,
  FEDEX_PRIORITY_OVERNIGHT_AK_HI: 3,
  FEDEX_STANDARD_OVERNIGHT: 4,
  FEDEX_PRIOTITY_OVERNIGHT_CONTINENTAL_US: 5,
  FEDEX_INTRA_CANADA_OVERNIGHT: 6,
  FEDEX_TWO_DAY: 7,
  FEDEX_SATURDAY: 8,
  FEDEX_INTERNATIONAL_ECONOMY: 9,
  WILLCALL: 10,
  KIOSK: 11,
  PICKUP: 12,
  EMAIL: 13,
  COURIER: 14,
  FEDEX_INTERNATIONAL_PRIORITY_PUERTO_RICO: 15,
  FEDEX_INTERNATIONAL_PRIORITY_CANADA: 16,
  PICKUP_EVENT_DAY: 17,
  OFFSITE_PICKUP: 18,
  HOSPITALITY: 19,
  UPS_WORLDWIDE_SAVER: 22,
  UPS_NEXT_BUSINESS_DAY_SAVER: 23,
  UPS_TWO_BUSINESS_DAYS: 24,
  UPS_NEXT_BUSINESS_DAY_AIR: 25,
  UPS_EXPRESS_SAVER_INTRA_CA: 27,
  UPS_STANDARD_INTRA: 36,
  ROYAL_MAIL: 37,
  DEUTSCHE_POST_INTRA_DE: 38,
  DEUTSCHE_POST_WORLDWIDE: 39,
  FLASHSEAT_INSTANT: 40,
  MOBILE_TICKET_INSTANT: 41,
  MOBILE_TICKET: 42,
  MOBILE_TRANSFER: 43,
  FLASH_TRANSFER: 44,
  FLASHSEAT: 45,
  SEASON_CARD_COURIER: 46,
  ELECTRONIC_AND_MOBILE_TICKET: 47,
  ELECTRONIC_AND_MOBILE_TICKET_INSTANT: 48,
  LOCAL_DELIVERY: 49,
  COURIER_WORLDWIDE: 50,
};

/**
 * List of delivery type IDs that are considered instant delivery.
 * @type {number[]}
 */
const instantDeliveryIds = [DELIVERY_TYPE_IDS.ELECTRONIC_INSTANT_DOWNLOAD, DELIVERY_TYPE_IDS.MOBILE_TICKET_INSTANT];
const fastDeliveryIds = [
  DELIVERY_TYPE_IDS.ELECTRONIC,
  DELIVERY_TYPE_IDS.MOBILE_TICKET,
  DELIVERY_TYPE_IDS.MOBILE_TRANSFER,
];

/**
 * Retrieves the icon name for a given delivery ID.
 * @param {number} deliveryId - The ID of the delivery method.
 * @returns {string} - The name of the icon corresponding to the delivery ID.
 * If the delivery ID is not found in the map, it returns 'GenericDisclosures'.
 */
const getDeliveryIcon = (deliveryId) => {
  let SelectedIcon = DELIVERY_ID_ICON_MAP[deliveryId];
  if (!SelectedIcon) {
    SelectedIcon = DELIVERY_ID_ICON_MAP.default;
  }

  return SelectedIcon;
};

/**
 * Checks if the provided delivery type(s) is/are instant delivery type(s).
 * This method can accept either an array of delivery types or a single delivery type ID.
 *
 * @param {Array|number} deliveryType - A single delivery type ID or an array of delivery type IDs to check.
 * @returns {boolean} True if any of the delivery types is an instant delivery type, otherwise false.
 */
const isInstantDelivery = (deliveryType) => {
  if (Array.isArray(deliveryType)) {
    return deliveryType.some((type) => instantDeliveryIds.includes(type));
  }

  return instantDeliveryIds.includes(deliveryType);
};

const isFastDelivery = (deliveryType, inHandDate) => {
  if (!deliveryType || !inHandDate) {
    return false;
  }

  // Calculate end of tomorrow (23:59:59.999 of tomorrow)
  const endOfTomorrow = new Date();
  endOfTomorrow.setDate(endOfTomorrow.getDate() + 1);
  endOfTomorrow.setHours(
    TIME.HOURS_OF_DAY - 1,
    TIME.MINS_OF_HOUR - 1,
    TIME.SECONDS_OF_MIN - 1,
    TIME.MILLIS_OF_SECOND - 1
  );

  const inHand = new Date(inHandDate);

  if (Array.isArray(deliveryType)) {
    return deliveryType.some((type) => fastDeliveryIds.includes(type)) && inHand <= endOfTomorrow;
  }

  return fastDeliveryIds.includes(deliveryType) && inHand <= endOfTomorrow;
};

export {
  getDeliveryIcon,
  isInstantDelivery,
  isFastDelivery,
  instantDeliveryIds,
  fastDeliveryIds,
  DELIVERY_TYPE_IDS,
  DELIVERY_ID_ICON_MAP,
  DELIVERY_METHOD_IDS,
};
